import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getResellerAvailableBillingItemsApi,
  getResellerAvailableBillingItemApi,
  createResellerAvailableBillingItemApi,
  setResellerAvailableBillingItemsApi,
  updateResellerAvailableBillingItemApi,
  deleteResellerAvailableBillingItemApi,
} from "../api/resellerAvailableBillingItemsApi";
import type { ResellerId } from "@KassaroAB/shared/types/ids";
import type { RestCreateResellerAvailableBillingItem, RestUpdateResellerAvailableBillingItem } from "@KassaroAB/shared/types/resellerAvailableBillingItem";

export const useGetResellerAvailableBillingItemsQuery = (resellerId: ResellerId | null) => {
  return useQuery({
    queryKey: ["resellers", resellerId, "availableBillingItems"],
    queryFn: () => getResellerAvailableBillingItemsApi(resellerId!),
    enabled: !!resellerId,
  });
};

export const useGetResellerAvailableBillingItemQuery = (
  resellerId: ResellerId | null,
  resellerAvailableBillingItemId: string | null,
) => {
  return useQuery({
    queryKey: ["resellers", resellerId, "availableBillingItems", resellerAvailableBillingItemId],
    queryFn: () => getResellerAvailableBillingItemApi(resellerId!, resellerAvailableBillingItemId!),
    enabled: !!resellerId && !!resellerAvailableBillingItemId,
  });
};

export const useCreateResellerAvailableBillingItemMutation = (resellerId: ResellerId | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: RestCreateResellerAvailableBillingItem) =>
      createResellerAvailableBillingItemApi(resellerId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["resellers", resellerId, "availableBillingItems"] });
    },
  });
};

export const useSetResellerAvailableBillingItemsMutation = (resellerId: ResellerId | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (items: RestCreateResellerAvailableBillingItem[]) =>
      setResellerAvailableBillingItemsApi(resellerId!, items),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["resellers", resellerId, "availableBillingItems"] });
    },
  });
};

export const useUpdateResellerAvailableBillingItemMutation = (resellerId: ResellerId | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      resellerAvailableBillingItemId,
      data,
    }: {
      resellerAvailableBillingItemId: string;
      data: RestUpdateResellerAvailableBillingItem;
    }) => updateResellerAvailableBillingItemApi(resellerId!, resellerAvailableBillingItemId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["resellers", resellerId, "availableBillingItems"] });
    },
  });
};

export const useDeleteResellerAvailableBillingItemMutation = (resellerId: ResellerId | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (resellerAvailableBillingItemId: string) =>
      deleteResellerAvailableBillingItemApi(resellerId!, resellerAvailableBillingItemId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["resellers", resellerId, "availableBillingItems"] });
    },
  });
};
